/**
 * Projections Filtering Utilities
 * 
 * Filters projection data to only show future years that don't have actual enrollment data.
 * This prevents backtest/historical projections from appearing in the production Projections Explorer.
 */

/**
 * Get the latest year with actual enrollment data
 * @param {Object} actualEnrollment - The actual_enrollment object with years as keys
 * @returns {number|null} - The latest year as an integer, or null if no data
 */
export const getLatestActualYear = (actualEnrollment) => {
  if (!actualEnrollment || typeof actualEnrollment !== 'object') {
    return null;
  }

  const years = Object.keys(actualEnrollment)
    .map(yearStr => {
      // Handle both "2023-2024" and "2023" formats
      const year = yearStr.includes('-') ? parseInt(yearStr.split('-')[0]) : parseInt(yearStr);
      return isNaN(year) ? null : year;
    })
    .filter(year => year !== null);

  return years.length > 0 ? Math.max(...years) : null;
};

/**
 * Filter projections to only include years greater than the latest actual year
 * @param {Object} projections - The projections object with scenarios (min, median, max, etc.)
 * @param {number} latestActualYear - The latest year with actual enrollment data
 * @returns {Object} - Filtered projections object
 */
export const filterFutureProjections = (projections, latestActualYear) => {
  if (!projections || typeof projections !== 'object' || !latestActualYear) {
    return projections;
  }

  const filtered = {};

  // Iterate through each scenario (min, median, max, outer_min, outer_max)
  Object.keys(projections).forEach(scenario => {
    const scenarioData = projections[scenario];
    
    if (typeof scenarioData !== 'object') {
      filtered[scenario] = scenarioData;
      return;
    }

    filtered[scenario] = {};

    // Filter years within this scenario
    Object.keys(scenarioData).forEach(yearStr => {
      // Parse the year from school year format "2024-2025" -> 2024
      const year = yearStr.includes('-') ? parseInt(yearStr.split('-')[0]) : parseInt(yearStr);
      
      // Only include if year is greater than latest actual year
      if (!isNaN(year) && year > latestActualYear) {
        filtered[scenario][yearStr] = scenarioData[yearStr];
      }
    });
  });

  return filtered;
};

/**
 * Filter enrollment data to only show future projections
 * Main function to be called with enrollment data from the API
 * @param {Object} enrollmentData - The full enrollment data object from API
 * @returns {Object} - Filtered enrollment data with only future projections
 */
export const filterEnrollmentDataForFutureOnly = (enrollmentData) => {
  if (!enrollmentData) {
    return enrollmentData;
  }

  // Get the latest year with actual enrollment
  const latestActualYear = getLatestActualYear(enrollmentData.actual_enrollment);

  if (!latestActualYear) {
    // If no actual data, return projections as-is
    return enrollmentData;
  }

  // Filter the projections
  const filteredProjections = filterFutureProjections(enrollmentData.projections, latestActualYear);

  // Return a new object with filtered projections
  return {
    ...enrollmentData,
    projections: filteredProjections
  };
};

